import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc';
import { TYPES as INDICATION_TYPES } from '@/engine/app/modules/indications/iocTypes';
import { type IObjectViewModel, type ObjectData, SceneItemKind } from './interfaces';
import type { IIndicationsViewModelFactory } from './modules/indications/interfaces';
import type { ILoadStateStrategy, IndicationData } from './IndicationsResolver';
import type { ViewEntity } from './models/viewEntity/view.entity.ts';
import { addVariableDistancesViewModels } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/VariableDistancesViewModelsHelper.ts';
import { addCustomCommissurePointsViewModels } from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/CustomCommissurePointsHelpers.ts';
import type { ViewsService } from './Views.service.ts';
import type { MeasurementData } from '@/engine/app/DiameterMeasurementsResolver';

export interface IStrategyByKindProvider {
  getStrategy: (kind: SceneItemKind) => ILoadStateStrategy;
}

@injectable()
export class LoadStateStrategiesProvider implements IStrategyByKindProvider {
  private readonly _vmFactory: IIndicationsViewModelFactory;

  private readonly _viewsService: ViewsService;

  public constructor(
    @inject(INDICATION_TYPES.IIndicationsViewModelFactory) vmFactory: IIndicationsViewModelFactory,
    @inject(TYPES.ViewsService) viewsService: ViewsService
  ) {
    this._vmFactory = vmFactory;
    this._viewsService = viewsService;
  }

  public getStrategy(kind: SceneItemKind): ILoadStateStrategy {
    switch (kind) {
      case SceneItemKind.IndicatedAngle:
      case SceneItemKind.IndicatedAnnotation:
      case SceneItemKind.IndicatedSpline:
      case SceneItemKind.IndicatedDistance: {
        return new ObjectsStrategy(this._vmFactory, kind);
      }
      case SceneItemKind.IndicatedSplineMeasurement:
      case SceneItemKind.IndicatedAreaMeasurement: {
        return new MeasurementsStrategy(this._vmFactory, kind);
      }
      case SceneItemKind.DistanceMeasurement:
      case SceneItemKind.LabeledDistanceMeasurement: {
        return new DistanceMeasurementsStrategy(this._viewsService, this._vmFactory, kind);
      }
      case SceneItemKind.CommissurePoint: {
        return new CommissurePointObjectsStrategy(this._viewsService, this._vmFactory, kind);
      }
      default: {
        throw new Error('Incorrect kind');
      }
    }
  }
}

class ObjectsStrategy implements ILoadStateStrategy {
  private readonly _vmFactory: IIndicationsViewModelFactory;

  private readonly _kind: SceneItemKind;

  public constructor(vmFactory: IIndicationsViewModelFactory, kind: SceneItemKind) {
    this._vmFactory = vmFactory;
    this._kind = kind;
  }

  public loadState(view: ViewEntity, indicationData: IndicationData): void {
    const viewModel = this._vmFactory.createViewModel(
      this._kind,
      indicationData.phaseId,
      indicationData.descriptor,
      indicationData.attachments,
      indicationData.label,
      undefined,
      indicationData.isVisible
    );
    view.addObject(viewModel);
  }
}

class CommissurePointObjectsStrategy implements ILoadStateStrategy {
  private readonly _vmFactory: IIndicationsViewModelFactory;

  private readonly _kind: SceneItemKind;

  private readonly _viewsService: ViewsService;

  public constructor(viewsService: ViewsService, vmFactory: IIndicationsViewModelFactory, kind: SceneItemKind) {
    this._vmFactory = vmFactory;
    this._kind = kind;
    this._viewsService = viewsService;
  }

  public loadState(view: ViewEntity, data: IndicationData): void {
    const indicationData = data as ObjectData;
    const viewModel: IObjectViewModel = this._vmFactory.createViewModel(
      this._kind,
      indicationData.phaseId,
      indicationData.descriptor,
      indicationData.attachments,
      indicationData.label,
      undefined,
      undefined,
      undefined,
      indicationData.color
    );

    addCustomCommissurePointsViewModels(view, this._viewsService, viewModel);
  }
}

class MeasurementsStrategy implements ILoadStateStrategy {
  private readonly _vmFactory: IIndicationsViewModelFactory;

  private readonly _kind: SceneItemKind;

  public constructor(vmFactory: IIndicationsViewModelFactory, kind: SceneItemKind) {
    this._vmFactory = vmFactory;
    this._kind = kind;
  }

  public loadState(view: ViewEntity, indicationData: IndicationData): void {
    const viewModel = this._vmFactory.createMeasurementViewModel(
      this._kind,
      indicationData.phaseId,
      indicationData.descriptor,
      indicationData.attachments,
      indicationData.label,
      undefined,
      indicationData.isVisible
    );
    view.addMeasurement(viewModel);
  }
}

class DistanceMeasurementsStrategy implements ILoadStateStrategy {
  private readonly _vmFactory: IIndicationsViewModelFactory;

  private readonly _kind: SceneItemKind;

  private readonly _viewsService: ViewsService;

  public constructor(viewsService: ViewsService, vmFactory: IIndicationsViewModelFactory, kind: SceneItemKind) {
    this._vmFactory = vmFactory;
    this._kind = kind;
    this._viewsService = viewsService;
  }

  public loadState(view: ViewEntity, data: IndicationData): void {
    const indicationData = data as MeasurementData;

    const distanceViewModel = this._vmFactory.createMeasurementViewModel(
      this._kind,
      indicationData.phaseId,
      indicationData.descriptor,
      indicationData.attachments,
      indicationData.label,
      undefined,
      undefined,
      indicationData.details
    );

    const pointViewModel = this._vmFactory.createViewModel(
      SceneItemKind.CuspPoint,
      indicationData.phaseId,
      indicationData.attachments[0],
      [indicationData.descriptor],
      undefined,
      undefined,
      undefined,
      undefined,
      (indicationData.attachmentData as {color: string}).color
    );
    addVariableDistancesViewModels(view, this._viewsService, distanceViewModel, pointViewModel);
  }
}
